import { useRef, useCallback, useEffect } from 'react';
import { logger } from '@fiverr-private/obs';

export const useSSE = <T = any>() => {
    const eventSourceRef = useRef<EventSource | null>(null);

    const start = useCallback(
        (
            url: string,
            {
                onMessage,
                onFinish,
                onError,
            }: {
                onMessage: (data: T) => void;
                onFinish: () => void;
                onError: () => void;
            }
        ) => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
            }

            const eventSource = new EventSource(url);
            eventSourceRef.current = eventSource;

            eventSource.onopen = () => {
                logger.info('SSE connection established.');
            };

            eventSource.onmessage = (event: MessageEvent) => {
                try {
                    if (!event.data) {
                        return;
                    }
                    const data = JSON.parse(event.data);
                    if (data.type === 'error') {
                        onError();
                    } else {
                        onMessage(data);
                    }
                } catch (error) {
                    logger.error('Failed to parse SSE message:', error as Error);
                }
            };

            eventSource.addEventListener('complete', () => {
                onFinish();
                eventSource.close();
            });

            eventSource.onerror = (err: Event) => {
                logger.error('SSE connection error:', err);
                eventSource.close();
            };
        },
        []
    );

    const stop = useCallback(() => {
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }
    }, []);

    useEffect(() => () => stop(), [stop]);

    return { start, stop };
};
